import React, { useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { InputTextarea } from "primereact/inputtextarea";
import { Panel } from "primereact/panel";
import { Tag } from "primereact/tag";
import { useLoaderData } from "react-router-dom";
import { getUserSettings } from "../../../data/accounts";
import { createMerchandise, createMerchandiseMovement, deleteMerchandise, deleteMerchandiseMovement, getMerchandise, getMerchandiseMovements, updateMerchandise, updateMerchandiseMovement } from "../../../data/loca/merchandise";
import { getWarehouses } from "../../../data/loca/warehouses";
import { notify, send } from "../../../services/NotificationService";

export async function loader() {
    const [items, warehouses, settings] = await Promise.all([getMerchandise(), getWarehouses(), getUserSettings()]);
    return { items, warehouses, settings };
}

const createEmptyItem = (settings) => ({
    id: null,
    name: "",
    description: "",
    category: "",
    isHidden: false,
    supplierName: "",
    sku: "",
    unit: settings?.defaultMerchandiseUnit ?? "pièce",
    quantityInStock: 0,
    reorderThreshold: settings?.defaultMerchandiseReorderThreshold ?? 0,
    unitCost: null,
    suggestedRetailPrice: null,
    priceTiers: [],
    lastRestockedAt: null,
    notes: "",
    warehouseId: null
});

const createMovementForm = () => ({
    quantity: 1,
    movedAt: new Date(),
    notes: ""
});

const UNCATEGORIZED = "Sans catégorie";
const HIDDEN_CATEGORY = "Caché";
const ALL_CATEGORIES = "__all__";

// Categories proposees par defaut : l'utilisateur peut en saisir d'autres, elles s'ajoutent a la liste.
const defaultCategorySuggestions = ["Glaces", "Barbe à papa", "Granités", "Popcorn", "Contenants & accessoires"];

const getCategoryLabel = (item) => item.isHidden ? HIDDEN_CATEGORY : ((item.category ?? "").trim() || UNCATEGORIZED);

// Couleurs imposees pour les categories courantes ; les autres gardent une teinte deduite de leur nom.
const namedCategoryTones = [
    [/barbe/, "rose"],
    [/contenant|accessoire/, "brown"],
    [/granit/, "turquoise"],
    [/pop.?corn/, "beige"],
    [/glace/, "yellow"]
];

const CATEGORY_TONES = 6;

const getCategoryTone = (category) => {
    if (category === UNCATEGORIZED) return "muted";
    if (category === HIDDEN_CATEGORY) return "hidden";

    const normalized = category.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const namedTone = namedCategoryTones.find(([pattern]) => pattern.test(normalized));
    if (namedTone) return namedTone[1];

    let hash = 0;
    for (let index = 0; index < category.length; index += 1) {
        hash = (hash * 31 + category.charCodeAt(index)) % 9973;
    }

    return String((hash % CATEGORY_TONES) + 1);
};

const movementDirectionOptions = [
    { label: "Entrée", value: "in" },
    { label: "Sortie", value: "out" }
];

const movementTypeLabels = {
    initial: "Stock initial",
    restock: "Entrée",
    removal: "Sortie",
    adjustment_in: "Ajustement +",
    adjustment_out: "Ajustement -",
    rental_option_out: "Sortie (location)",
    rental_option_adjust: "Ajustement (location)",
    rental_option_cancel: "Remise en stock (annulation)"
};

export default function MerchandisePage() {
    const { items: initialItems, warehouses, settings } = useLoaderData();
    const [items, setItems] = useState(initialItems ?? []);
    const [form, setForm] = useState(createEmptyItem(settings));
    const [movements, setMovements] = useState({});
    const [search, setSearch] = useState("");
    const [categoryFilter, setCategoryFilter] = useState(ALL_CATEGORIES);
    const [savingItem, setSavingItem] = useState(false);
    const [formVisible, setFormVisible] = useState(false);
    const [togglingHiddenId, setTogglingHiddenId] = useState(null);
    const categoryDropdownRef = useRef(null);
    const [savingMovementId, setSavingMovementId] = useState(null);
    const [deletingMovementId, setDeletingMovementId] = useState(null);
    const [loadingHistoryId, setLoadingHistoryId] = useState(null);
    const [editingMovement, setEditingMovement] = useState(null);
    const [movementEditForm, setMovementEditForm] = useState(createMovementForm());
    const [savingMovementEdit, setSavingMovementEdit] = useState(false);

    const warehouseOptions = [{ label: "Aucun entrepôt", value: null }, ...(warehouses ?? []).map((warehouse) => ({
        label: `${warehouse.name} — ${warehouse.city}`,
        value: warehouse.id
    }))];

    // Les marchandises cachees sortent des compteurs, des groupes et de la liste d'achats :
    // on ne les retrouve qu'en cliquant la pastille « Caché ».
    const visibleItems = useMemo(() => items.filter((item) => !item.isHidden), [items]);
    const hiddenItems = useMemo(() => items.filter((item) => item.isHidden), [items]);

    // La pastille « Caché » disparait quand plus rien n'est cache : on revient alors sur « Toutes ».
    const activeCategory = categoryFilter === HIDDEN_CATEGORY && hiddenItems.length === 0 ? ALL_CATEGORIES : categoryFilter;

    const summary = useMemo(() => {
        const totalReferences = visibleItems.length;
        const totalUnits = visibleItems.reduce((sum, item) => sum + (item.quantityInStock ?? 0), 0);
        const lowStockCount = visibleItems.filter((item) => item.needsReorder).length;
        const totalValue = visibleItems.reduce((sum, item) => sum + (item.stockValue ?? 0), 0);
        return { totalReferences, totalUnits, lowStockCount, totalValue };
    }, [visibleItems]);

    // Categories disponibles : celles deja utilisees plus les suggestions, pour alimenter le filtre et le formulaire.
    const usedCategories = useMemo(() => Array.from(new Set(visibleItems.map(getCategoryLabel)))
        .filter((category) => category !== UNCATEGORIZED)
        .sort((a, b) => a.localeCompare(b, "fr-FR", { sensitivity: "base" })), [visibleItems]);

    const categorySuggestions = useMemo(() => Array.from(new Set([...usedCategories, ...defaultCategorySuggestions]))
        .sort((a, b) => a.localeCompare(b, "fr-FR", { sensitivity: "base" })), [usedCategories]);

    const categoryOptions = useMemo(() => categorySuggestions.map((category) => ({
        label: category,
        value: category,
        tone: getCategoryTone(category)
    })), [categorySuggestions]);

    const categoryFilterOptions = useMemo(() => {
        const countByCategory = visibleItems.reduce((counts, item) => {
            const category = getCategoryLabel(item);
            return { ...counts, [category]: (counts[category] ?? 0) + 1 };
        }, {});

        const options = usedCategories.map((category) => ({
            label: category,
            value: category,
            count: countByCategory[category],
            tone: getCategoryTone(category)
        }));

        if (countByCategory[UNCATEGORIZED]) {
            options.push({ label: UNCATEGORIZED, value: UNCATEGORIZED, count: countByCategory[UNCATEGORIZED], tone: "muted" });
        }

        if (hiddenItems.length) {
            options.push({ label: HIDDEN_CATEGORY, value: HIDDEN_CATEGORY, count: hiddenItems.length, tone: "hidden" });
        }

        return [{ label: "Toutes", value: ALL_CATEGORIES, count: visibleItems.length, tone: "all" }, ...options];
    }, [visibleItems, hiddenItems, usedCategories]);

    const filteredItems = useMemo(() => {
        const query = search.trim().toLowerCase();
        const scope = activeCategory === HIDDEN_CATEGORY ? hiddenItems : visibleItems;
        const matchesCategory = (item) => activeCategory === ALL_CATEGORIES || getCategoryLabel(item) === activeCategory;
        if (!query) return scope.filter(matchesCategory);

        return scope.filter((item) => matchesCategory(item) && [item.name, item.sku, item.description, item.notes, item.supplierName, item.warehouseName, item.category]
            .filter(Boolean)
            .some((value) => value.toLowerCase().includes(query)));
    }, [visibleItems, hiddenItems, search, activeCategory]);

    // Le stock est presente par categorie : "Sans categorie" ferme la marche.
    const groupedItems = useMemo(() => {
        const groups = new Map();
        filteredItems.forEach((item) => {
            const category = getCategoryLabel(item);
            if (!groups.has(category)) groups.set(category, []);
            groups.get(category).push(item);
        });

        return Array.from(groups.entries())
            .map(([category, categoryItems]) => ({
                category,
                tone: getCategoryTone(category),
                items: categoryItems,
                totalUnits: categoryItems.reduce((sum, item) => sum + (item.quantityInStock ?? 0), 0),
                lowStockCount: categoryItems.filter((item) => item.needsReorder).length
            }))
            .sort((a, b) => {
                if (a.category === UNCATEGORIZED) return 1;
                if (b.category === UNCATEGORIZED) return -1;
                return a.category.localeCompare(b.category, "fr-FR", { sensitivity: "base" });
            });
    }, [filteredItems]);

    // Filtre sur une seule categorie : le compte tient sur la ligne des pastilles, l'en-tete de groupe ferait doublon.
    const singleGroup = activeCategory !== ALL_CATEGORIES && groupedItems.length === 1 ? groupedItems[0] : null;

    const purchaseList = useMemo(() => visibleItems
        .filter((item) => item.needsReorder)
        .map((item) => {
            const targetStock = Math.max(1, item.reorderThreshold ?? 0);
            const quantityToBuy = Math.max(1, targetStock - (item.quantityInStock ?? 0));
            return {
                ...item,
                quantityToBuy
            };
        })
        .sort((a, b) => b.quantityToBuy - a.quantityToBuy), [visibleItems]);

    const sortItems = (nextItems) => [...nextItems].sort((a, b) => a.name.localeCompare(b.name));

    const onChange = (field, value) => setForm((current) => ({ ...current, [field]: value }));

    const resetForm = () => setForm(createEmptyItem(settings));

    const showError = (message) => {
        send("error", message);
        notify();
    };

    const ensureSavedItem = (saved) => {
        if (!saved?.id) {
            throw new Error(saved?.title || saved?.error || "La marchandise n'a pas pu etre enregistree.");
        }

        return saved;
    };

    const updateItemInList = (saved) => {
        const normalizedSaved = ensureSavedItem(saved);
        setItems((current) => sortItems(current.some((item) => item.id === normalizedSaved.id)
            ? current.map((item) => item.id === normalizedSaved.id ? normalizedSaved : item)
            : [...current, normalizedSaved]));
    };

    const updateItemMovementsInList = (itemId, nextMovements) => {
        setItems((current) => current.map((item) => item.id === itemId
            ? { ...item, movements: nextMovements }
            : item));
    };

    const loadMovementHistory = async (item) => {
        if (item.movements || loadingHistoryId === item.id) return;

        setLoadingHistoryId(item.id);
        try {
            const nextMovements = await getMerchandiseMovements(item.id);
            updateItemMovementsInList(item.id, nextMovements);
        } catch (error) {
            showError(error?.message || "L'historique des mouvements n'a pas pu être chargé.");
        } finally {
            setLoadingHistoryId(null);
        }
    };

    const onHistoryToggle = (event, item) => {
        if (event.currentTarget.open) {
            loadMovementHistory(item);
        }
    };

    const getMovementForm = (itemId) => ({
        ...createMovementForm(),
        ...(movements[itemId] ?? {})
    });

    const getPurchaseMovementKey = (item) => `purchase-${item.id}`;

    const getPurchaseMovementForm = (item) => ({
        ...createMovementForm(),
        quantity: item.quantityToBuy,
        notes: "Réapprovisionnement depuis la liste d'achat",
        ...(movements[getPurchaseMovementKey(item)] ?? {})
    });

    const onMovementChange = (itemId, field, value) => {
        setMovements((current) => ({
            ...current,
            [itemId]: {
                ...createMovementForm(),
                ...(current[itemId] ?? {}),
                [field]: value
            }
        }));
    };

    const getMerchandiseDto = (item) => ({
        name: item.name,
        description: item.description,
        category: (item.category ?? "").trim() || null,
        isHidden: !!item.isHidden,
        supplierName: item.supplierName,
        sku: item.sku,
        unit: item.unit,
        quantityInStock: item.quantityInStock ?? 0,
        reorderThreshold: item.reorderThreshold ?? 0,
        unitCost: item.unitCost,
        suggestedRetailPrice: item.suggestedRetailPrice,
        priceTiers: (item.priceTiers ?? []).map((tier) => ({ minimumQuantity: tier.minimumQuantity, unitPrice: tier.unitPrice })),
        lastRestockedAt: item.lastRestockedAt,
        notes: item.notes,
        warehouseId: item.warehouseId
    });

    const submitMovement = async (item, direction) => {
        const movement = getMovementForm(item.id);
        if (!movement.quantity || movement.quantity <= 0) {
            send("warning", "Indiquez une quantité supérieure à 0.");
            notify();
            return;
        }

        setSavingMovementId(item.id);
        try {
            const saved = await createMerchandiseMovement(item.id, {
                quantity: movement.quantity,
                direction,
                movedAt: movement.movedAt,
                notes: movement.notes
            });

            updateItemInList(saved);
            setMovements((current) => ({
                ...current,
                [item.id]: createMovementForm()
            }));
            send("success", direction === "in" ? `Entrée de stock ajoutée pour ${item.name}.` : `Sortie de stock ajoutée pour ${item.name}.`);
            notify();
        } catch (error) {
            showError(error?.message || "Le mouvement de stock n'a pas pu être ajouté.");
        } finally {
            setSavingMovementId(null);
        }
    };

    const submitPurchaseRestock = async (item) => {
        const movementKey = getPurchaseMovementKey(item);
        const movement = getPurchaseMovementForm(item);
        if (!movement.quantity || movement.quantity <= 0) {
            send("warning", "Indiquez une quantité supérieure à 0.");
            notify();
            return;
        }

        setSavingMovementId(movementKey);
        try {
            const saved = await createMerchandiseMovement(item.id, {
                quantity: movement.quantity,
                direction: "in",
                movedAt: movement.movedAt,
                notes: movement.notes
            });

            updateItemInList(saved);
            setMovements((current) => ({
                ...current,
                [movementKey]: createMovementForm()
            }));
            send("success", `Entrée de stock ajoutée pour ${item.name}.`);
            notify();
        } catch (error) {
            showError(error?.message || "Le mouvement de stock n'a pas pu être ajouté.");
        } finally {
            setSavingMovementId(null);
        }
    };

    const onSubmit = async () => {
        if (!form.name?.trim()) {
            send("warning", "Le nom de la marchandise est obligatoire.");
            notify();
            return;
        }

        setSavingItem(true);
        try {
            const dto = getMerchandiseDto(form);

            const saved = form.id ? await updateMerchandise(form.id, dto) : await createMerchandise(dto);
            updateItemInList(saved);
            resetForm();
            setFormVisible(false);
            send("success", form.id ? "Marchandise mise à jour." : "Marchandise ajoutée.");
            notify();
        } catch (error) {
            showError(error?.message || "La marchandise n'a pas pu être enregistrée.");
        } finally {
            setSavingItem(false);
        }
    };

    // Creation comme modification passent par la meme fenetre : plus besoin de descendre en bas de page.
    const onEdit = (item) => {
        setForm({
            ...item,
            priceTiers: (item.priceTiers ?? []).map((tier) => ({ minimumQuantity: tier.minimumQuantity, unitPrice: tier.unitPrice })),
            lastRestockedAt: item.lastRestockedAt ? new Date(item.lastRestockedAt) : null
        });
        setFormVisible(true);
    };

    const onAdd = () => {
        resetForm();
        setFormVisible(true);
    };

    const closeForm = () => {
        if (savingItem) return;
        setFormVisible(false);
        resetForm();
    };

    const updatePriceTier = (index, field, value) => setForm((current) => ({
        ...current,
        priceTiers: current.priceTiers.map((tier, tierIndex) => tierIndex === index ? { ...tier, [field]: value } : tier)
    }));

    const categoryOptionTemplate = (option) => (
        <span className={`inventory-badge inventory-badge--sm inventory-badge--tone-${option.tone}`}>{option.label}</span>
    );

    // Un clic n'importe ou sur le champ deplie la liste ; le chevron garde son propre bascule.
    const openCategoryDropdown = () => {
        if (!categoryDropdownRef.current?.getOverlay?.()) {
            categoryDropdownRef.current?.show();
        }
    };

    const onToggleHidden = async (item) => {
        setTogglingHiddenId(item.id);
        try {
            const saved = await updateMerchandise(item.id, { ...getMerchandiseDto(item), isHidden: !item.isHidden });
            updateItemInList(saved);
            send("success", item.isHidden ? `${item.name} est de nouveau visible.` : `${item.name} est masquée.`);
            notify();
        } catch (error) {
            showError(error?.message || "La marchandise n'a pas pu être masquée.");
        } finally {
            setTogglingHiddenId(null);
        }
    };

    const onDuplicate = async (item) => {
        const saved = await createMerchandise({
            ...getMerchandiseDto(item),
            name: `${item.name} (copie)`
        });

        updateItemInList(saved);
        send("success", `Marchandise dupliquée : ${saved.name}.`);
        notify();
    };

    const onDelete = async (item) => {
        await deleteMerchandise(item.id);
        setItems((current) => current.filter((entry) => entry.id !== item.id));
        if (form.id === item.id) resetForm();
        send("success", "Marchandise supprimée.");
        notify();
    };

    const onDeleteMovement = async (item, entry) => {
        const confirmed = window.confirm("Supprimer ce mouvement de stock ? Le stock de la marchandise sera recalculé.");
        if (!confirmed) return;

        setDeletingMovementId(entry.id);
        try {
            const saved = await deleteMerchandiseMovement(item.id, entry.id);
            updateItemInList(saved);
            send("success", "Mouvement de stock supprimé.");
            notify();
        } catch (error) {
            showError(error?.message || "Le mouvement de stock n'a pas pu être supprimé.");
        } finally {
            setDeletingMovementId(null);
        }
    };

    const onEditMovement = (item, entry) => {
        setEditingMovement({ item, entry });
        setMovementEditForm({
            quantity: Math.abs(entry.quantityDelta ?? 1),
            direction: entry.quantityDelta > 0 ? "in" : "out",
            movedAt: entry.createdAt ? new Date(entry.createdAt) : new Date(),
            notes: entry.notes ?? ""
        });
    };

    const onMovementEditChange = (field, value) => {
        setMovementEditForm((current) => ({
            ...current,
            [field]: value
        }));
    };

    const onPurchaseMovementChange = (item, field, value) => {
        const movementKey = getPurchaseMovementKey(item);
        setMovements((current) => ({
            ...current,
            [movementKey]: {
                ...getPurchaseMovementForm(item),
                ...(current[movementKey] ?? {}),
                [field]: value
            }
        }));
    };

    const closeMovementEdit = () => {
        if (savingMovementEdit) return;
        setEditingMovement(null);
        setMovementEditForm(createMovementForm());
    };

    const submitMovementEdit = async () => {
        if (!editingMovement) return;
        if (!movementEditForm.quantity || movementEditForm.quantity <= 0) {
            send("warning", "Indiquez une quantité supérieure à 0.");
            notify();
            return;
        }

        setSavingMovementEdit(true);
        try {
            const saved = await updateMerchandiseMovement(editingMovement.item.id, editingMovement.entry.id, {
                quantity: movementEditForm.quantity,
                direction: movementEditForm.direction,
                movedAt: movementEditForm.movedAt,
                notes: movementEditForm.notes
            });

            updateItemInList(saved);
            setEditingMovement(null);
            setMovementEditForm(createMovementForm());
            send("success", "Mouvement de stock modifié.");
            notify();
        } catch (error) {
            showError(error?.message || "Le mouvement de stock n'a pas pu être modifié.");
        } finally {
            setSavingMovementEdit(false);
        }
    };

    const merchandiseFormFooter = (
        <div className="contentForm">
            <Button type="button" severity="secondary" outlined disabled={savingItem} onClick={closeForm}>Annuler</Button>
            <Button type="button" loading={savingItem} disabled={savingItem} onClick={onSubmit}>{form.id ? "Mettre à jour" : "Ajouter"}</Button>
        </div>
    );

    const movementEditFooter = (
        <div className="contentForm">
            <Button type="button" severity="secondary" outlined disabled={savingMovementEdit} onClick={closeMovementEdit}>Annuler</Button>
            <Button type="button" loading={savingMovementEdit} disabled={savingMovementEdit} onClick={submitMovementEdit}>Enregistrer</Button>
        </div>
    );

    return (
        <div>
            <h1>Gestion des stocks de marchandise</h1>
            <div className="inventory-summary-grid">
                <div className="inventory-summary-card">
                    <span>Références</span>
                    <strong>{summary.totalReferences}</strong>
                </div>
                <div className="inventory-summary-card">
                    <span>Unités en stock</span>
                    <strong>{summary.totalUnits}</strong>
                </div>
                <div className="inventory-summary-card is-alert">
                    <span>À réapprovisionner</span>
                    <strong>{summary.lowStockCount}</strong>
                </div>
                <div className="inventory-summary-card">
                    <span>Valeur estimée</span>
                    <strong>{summary.totalValue.toFixed(2)} €</strong>
                </div>
            </div>

            <Panel header="Stock marchandises">
                <div className="contentForm">
                    <span className="p-float-label">
                        <InputText id="merchandise-search" value={search} onChange={(e) => setSearch(e.target.value)} />
                        <label htmlFor="merchandise-search">Rechercher une marchandise</label>
                    </span>
                    <Button type="button" icon="pi pi-plus" label="Ajouter une marchandise" onClick={onAdd} />
                </div>
                <div className="inventory-category-filters">
                    {categoryFilterOptions.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            className={`inventory-chip inventory-chip--tone-${option.tone}${activeCategory === option.value ? " is-active" : ""}`}
                            onClick={() => setCategoryFilter(option.value)}
                        >
                            <span>{option.label}</span>
                            <span className="inventory-chip__count">{option.count}</span>
                        </button>
                    ))}
                    {singleGroup ? (
                        <span className="inventory-category-filters__summary">
                            {singleGroup.items.length} référence{singleGroup.items.length > 1 ? "s" : ""} • {singleGroup.totalUnits} unités
                            {singleGroup.lowStockCount ? <span className="inventory-badge inventory-badge--alert">{singleGroup.lowStockCount} à réapprovisionner</span> : null}
                        </span>
                    ) : null}
                </div>
                {filteredItems.length === 0 ? <p>{items.length === 0 ? "Aucune marchandise enregistrée." : "Aucune marchandise ne correspond à votre recherche."}</p> : groupedItems.map((group) => (
                    <div key={group.category} className="inventory-category">
                        {singleGroup ? null : (
                            <div className="inventory-category__header">
                                <span className={`inventory-badge inventory-badge--lg inventory-badge--tone-${group.tone}`}>{group.category}</span>
                                <span className="inventory-category__meta">
                                    {group.items.length} référence{group.items.length > 1 ? "s" : ""} • {group.totalUnits} unités
                                </span>
                                {group.lowStockCount ? <span className="inventory-badge inventory-badge--alert">{group.lowStockCount} à réapprovisionner</span> : null}
                            </div>
                        )}
                        {group.items.map((item) => {
                    const movement = getMovementForm(item.id);
                    const isSavingMovement = savingMovementId === item.id;

                    return (
                        <details key={item.id} className={`inventory-card inventory-card--tone-${group.tone}`}>
                            <summary className="inventory-card__summary">
                                <div className="inventory-card__header">
                                    <div>
                                        <b>{item.name}</b>
                                    </div>
                                    <div className="inventory-card__header-right">
                                        <div className="inventory-card__header-stats">
                                            <div className="inventory-card__pill">
                                                <span>Stock</span>
                                                <strong>{item.quantityInStock} {item.unit}</strong>
                                            </div>
                                            <div className="inventory-card__pill">
                                                <span>Prix de vente conseillé</span>
                                                <strong>{item.suggestedRetailPrice != null ? `${item.suggestedRetailPrice.toFixed(2)} €` : "—"}</strong>
                                            </div>
                                        </div>
                                        <div className="inventory-card__summary-actions">
                                            <Tag severity={item.needsReorder ? "danger" : "success"} value={item.needsReorder ? "Réapprovisionnement" : "Stock OK"} />
                                            <span className="inventory-card__toggle">Déplier</span>
                                        </div>
                                    </div>
                                </div>
                            </summary>
                            <div className="inventory-card__content">
                                <div className="inventory-card__grid">
                                    <div><span>Stock</span><strong>{item.quantityInStock} {item.unit}</strong></div>
                                    <div><span>Seuil</span><strong>{item.reorderThreshold} {item.unit}</strong></div>
                                    <div><span>Entrepôt</span><strong>{item.warehouseName || "Non affecté"}</strong></div>
                                    <div><span>Valeur estimée</span><strong>{item.stockValue != null ? `${item.stockValue.toFixed(2)} €` : "—"}</strong></div>
                                    <div><span>Prix de vente conseillé</span><strong>{item.suggestedRetailPrice != null ? `${item.suggestedRetailPrice.toFixed(2)} €` : "—"}</strong></div>
                                </div>
                                {(item.priceTiers ?? []).length > 0 && (
                                    <div className="inventory-card__meta">
                                        <b>Tarifs dégressifs :</b>{' '}
                                        {item.priceTiers.map((tier) => `${tier.minimumQuantity}+ ${item.unit} : ${Number(tier.unitPrice).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}/unité`).join(' · ')}
                                    </div>
                                )}
                                {item.description ? <p>{item.description}</p> : null}
                                {item.notes ? <div className="inventory-card__notes">{item.notes}</div> : null}
                                <div className="inventory-card__meta">
                                    Dernier réapprovisionnement : {item.lastRestockedAt ? new Date(item.lastRestockedAt).toLocaleDateString("fr-FR") : "non renseigné"}
                                </div>
                                <div className="inventory-movement-card inventory-movement-card--compact">
                                    <div className="inventory-movement-card__header">
                                        <strong>Mouvement de stock</strong>
                                        <span className="inventory-card__meta">Entrée ou sortie rapide</span>
                                    </div>
                                    <div className="inventory-movement-form">
                                        <span className="p-float-label">
                                            <InputNumber inputId={`movement-quantity-${item.id}`} value={movement.quantity} onValueChange={(e) => onMovementChange(item.id, "quantity", e.value ?? 0)} useGrouping={false} min={1} />
                                            <label htmlFor={`movement-quantity-${item.id}`}>Quantité</label>
                                        </span>
                                        <span className="p-float-label">
                                            <Calendar inputId={`movement-date-${item.id}`} value={movement.movedAt} onChange={(e) => onMovementChange(item.id, "movedAt", e.value)} showIcon dateFormat="dd/mm/yy" />
                                            <label htmlFor={`movement-date-${item.id}`}>Date</label>
                                        </span>
                                        <span className="p-float-label inventory-movement-notes">
                                            <InputText id={`movement-notes-${item.id}`} value={movement.notes} onChange={(e) => onMovementChange(item.id, "notes", e.target.value)} />
                                            <label htmlFor={`movement-notes-${item.id}`}>Note</label>
                                        </span>
                                        <div className="inventory-movement-actions">
                                            <Button type="button" size="small" severity="success" outlined loading={isSavingMovement} disabled={isSavingMovement} onClick={() => submitMovement(item, "in")}>+ Stock</Button>
                                            <Button type="button" size="small" severity="warning" outlined disabled={isSavingMovement} onClick={() => submitMovement(item, "out")}>- Stock</Button>
                                        </div>
                                    </div>
                                </div>
                                <details className="inventory-history" onToggle={(event) => onHistoryToggle(event, item)}>
                                    <summary className="inventory-history__summary">
                                        <span className="inventory-history__title">Historique des mouvements</span>
                                    </summary>
                                    <div className="inventory-history__content">
                                        {loadingHistoryId === item.id ? (
                                            <div className="inventory-card__meta">Chargement de l'historique...</div>
                                        ) : !item.movements ? (
                                            <div className="inventory-card__meta">Ouvrez l'historique pour charger les mouvements.</div>
                                        ) : item.movements.length ? item.movements.map((entry) => (
                                            <div key={entry.id} className="inventory-history__row">
                                                <div>
                                                    <strong>{movementTypeLabels[entry.movementType] ?? entry.movementType}</strong>
                                                    <div className="inventory-card__meta">{new Date(entry.createdAt).toLocaleString("fr-FR")}{entry.notes ? ` • ${entry.notes}` : ""}</div>
                                                </div>
                                                <div className="inventory-history__values">
                                                    <span className={entry.quantityDelta > 0 ? "inventory-delta inventory-delta--positive" : "inventory-delta inventory-delta--negative"}>
                                                        {entry.quantityDelta > 0 ? "+" : ""}{entry.quantityDelta}
                                                    </span>
                                                    <span className="inventory-card__meta">Stock: {entry.stockAfterMovement}</span>
                                                    <div className="inventory-history__actions">
                                                        <Button type="button" icon="pi pi-pencil" rounded text severity="secondary" aria-label="Modifier le mouvement" disabled={savingMovementEdit} onClick={() => onEditMovement(item, entry)} />
                                                        <Button type="button" icon="pi pi-trash" rounded text severity="danger" aria-label="Supprimer le mouvement" loading={deletingMovementId === entry.id} disabled={deletingMovementId === entry.id} onClick={() => onDeleteMovement(item, entry)} />
                                                    </div>
                                                </div>
                                            </div>
                                        )) : <div className="inventory-card__meta">Aucun mouvement enregistré.</div>}
                                    </div>
                                </details>
                                <div className="contentForm inventory-card__actions">
                                    <Button type="button" severity="secondary" onClick={() => onEdit(item)}>Modifier</Button>
                                    <Button type="button" severity="contrast" outlined onClick={() => onDuplicate(item)}>Dupliquer</Button>
                                    <Button type="button" severity="secondary" outlined loading={togglingHiddenId === item.id} disabled={togglingHiddenId === item.id} onClick={() => onToggleHidden(item)}>{item.isHidden ? "Réafficher" : "Masquer"}</Button>
                                    <Button type="button" severity="danger" onClick={() => onDelete(item)}>Supprimer</Button>
                                </div>
                            </div>
                        </details>
                    );
                        })}
                    </div>
                ))}
            </Panel>

            <Panel header="Liste des marchandises à acheter" className="inventory-panel-subtle">
                {purchaseList.length === 0 ? (
                    <p>Aucun achat recommandé pour le moment.</p>
                ) : (
                    <div>
                        <p className="inventory-panel-subtitle">Les marchandises ci-dessous sont en rupture ou sous le seuil recommandé.</p>
                        {purchaseList.map((item) => {
                            const movement = getPurchaseMovementForm(item);
                            const movementKey = getPurchaseMovementKey(item);
                            const isSavingMovement = savingMovementId === movementKey;

                            return (
                            <div key={movementKey} className="inventory-history__row">
                                <div>
                                    <div className="inventory-card__identity">
                                        <strong>{item.name}</strong>
                                        <span className={`inventory-badge inventory-badge--sm inventory-badge--tone-${getCategoryTone(getCategoryLabel(item))}`}>{getCategoryLabel(item)}</span>
                                    </div>
                                    <div className="inventory-card__meta">
                                        Stock actuel : {item.quantityInStock} {item.unit} • Seuil : {item.reorderThreshold} {item.unit}
                                    </div>
                                </div>
                                <div className="inventory-history__values">
                                    <span className="inventory-delta inventory-delta--negative">À acheter : {item.quantityToBuy} {item.unit}</span>
                                    <div className="inventory-movement-actions">
                                        <span className="p-float-label">
                                            <InputNumber inputId={`purchase-quantity-${item.id}`} value={movement.quantity} onValueChange={(e) => onPurchaseMovementChange(item, "quantity", e.value ?? 0)} useGrouping={false} min={1} />
                                            <label htmlFor={`purchase-quantity-${item.id}`}>Quantité</label>
                                        </span>
                                        <Button type="button" size="small" severity="success" outlined loading={isSavingMovement} disabled={isSavingMovement} onClick={() => submitPurchaseRestock(item)}>+ Stock</Button>
                                    </div>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                )}
            </Panel>

            <Dialog
                header={form.id ? "Modifier la marchandise" : "Ajouter une marchandise"}
                visible={formVisible}
                modal
                className="inventory-dialog"
                style={{ width: "min(760px, 95vw)" }}
                contentStyle={{ paddingTop: "1.5rem" }}
                footer={merchandiseFormFooter}
                onHide={closeForm}
            >
                <div className="contentForm">
                    <span className="p-float-label">
                        <InputText id="merchandise-name" value={form.name} onChange={(e) => onChange("name", e.target.value)} />
                        <label htmlFor="merchandise-name">Nom</label>
                    </span>
                    <span className="p-float-label">
                        <InputText id="merchandise-supplier" value={form.supplierName ?? ""} onChange={(e) => onChange("supplierName", e.target.value)} />
                        <label htmlFor="merchandise-supplier">Fournisseur</label>
                    </span>
                </div>
                <div className="contentForm">
                    <span className="p-float-label">
                        <Dropdown
                            ref={categoryDropdownRef}
                            inputId="merchandise-category"
                            value={form.category ?? ""}
                            options={categoryOptions}
                            optionLabel="label"
                            optionValue="value"
                            itemTemplate={categoryOptionTemplate}
                            onChange={(e) => onChange("category", e.value ?? "")}
                            onClick={openCategoryDropdown}
                            editable
                            panelClassName="inventory-category-dropdown-panel"
                            className={`inventory-dropdown inventory-category-dropdown inventory-category-dropdown--tone-${getCategoryTone((form.category ?? "").trim() || UNCATEGORIZED)}`}
                        />
                        <label htmlFor="merchandise-category">Catégorie</label>
                    </span>
                </div>
                <div className="contentForm inventory-dialog__switch">
                    <InputSwitch inputId="merchandise-hidden" checked={!!form.isHidden} onChange={(e) => onChange("isHidden", e.value)} />
                    <label htmlFor="merchandise-hidden">Masquer cette marchandise — elle sort de la liste et de la liste d'achats, son stock est conservé</label>
                </div>
                <div className="contentForm">
                    <span className="p-float-label">
                        <InputText id="merchandise-sku" value={form.sku ?? ""} onChange={(e) => onChange("sku", e.target.value)} />
                        <label htmlFor="merchandise-sku">Référence / SKU</label>
                    </span>
                    <span className="p-float-label">
                        <InputText id="merchandise-unit" value={form.unit ?? "pièce"} onChange={(e) => onChange("unit", e.target.value)} />
                        <label htmlFor="merchandise-unit">Unité</label>
                    </span>
                </div>
                <div className="contentForm">
                    <span className="p-float-label">
                        <InputNumber inputId="merchandise-stock" value={form.quantityInStock} onValueChange={(e) => onChange("quantityInStock", e.value ?? 0)} useGrouping={false} />
                        <label htmlFor="merchandise-stock">Stock restant</label>
                    </span>
                    <span className="p-float-label">
                        <InputNumber inputId="merchandise-threshold" value={form.reorderThreshold} onValueChange={(e) => onChange("reorderThreshold", e.value ?? 0)} useGrouping={false} />
                        <label htmlFor="merchandise-threshold">Seuil d'alerte</label>
                    </span>
                </div>
                <div className="contentForm">
                    <span className="p-float-label">
                        <InputNumber inputId="merchandise-cost" value={form.unitCost} onValueChange={(e) => onChange("unitCost", e.value)} mode="currency" currency="EUR" locale="fr-FR" min={0} />
                        <label htmlFor="merchandise-cost">Coût unitaire</label>
                    </span>
                    <span className="p-float-label">
                        <InputNumber inputId="merchandise-suggested-price" value={form.suggestedRetailPrice} onValueChange={(e) => onChange("suggestedRetailPrice", e.value)} mode="currency" currency="EUR" locale="fr-FR" min={0} />
                        <label htmlFor="merchandise-suggested-price">Prix de vente conseillé</label>
                    </span>
                </div>
                <div className="inventory-dialog__tiers">
                    <span className="inventory-dialog__tiers-title">Tarifs dégressifs</span>
                    {(form.priceTiers ?? []).map((tier, index) => (
                        <div key={index} className="inventory-dialog__tier">
                            <InputNumber value={tier.minimumQuantity} onValueChange={(e) => updatePriceTier(index, 'minimumQuantity', e.value ?? 1)} useGrouping={false} min={1} />
                            <span>unités :</span>
                            <InputNumber value={tier.unitPrice} onValueChange={(e) => updatePriceTier(index, 'unitPrice', e.value ?? 0)} mode="currency" currency="EUR" locale="fr-FR" min={0} />
                            <Button type="button" icon="pi pi-trash" severity="danger" text aria-label="Supprimer le palier" onClick={() => setForm((current) => ({ ...current, priceTiers: current.priceTiers.filter((_, tierIndex) => tierIndex !== index) }))} />
                        </div>
                    ))}
                    <Button type="button" label="Ajouter un palier" icon="pi pi-plus" text onClick={() => setForm((current) => ({ ...current, priceTiers: [...current.priceTiers, { minimumQuantity: 1, unitPrice: current.suggestedRetailPrice ?? 0 }] }))} />
                </div>
                <div className="contentForm">
                    <span className="p-float-label">
                        <Calendar inputId="merchandise-restocked" value={form.lastRestockedAt} onChange={(e) => onChange("lastRestockedAt", e.value)} showIcon dateFormat="dd/mm/yy" />
                        <label htmlFor="merchandise-restocked">Dernier réapprovisionnement</label>
                    </span>
                </div>
                <div className="contentForm">
                    <span className="p-float-label">
                        <Dropdown inputId="merchandise-warehouse" value={form.warehouseId} options={warehouseOptions} onChange={(e) => onChange("warehouseId", e.value)} className="inventory-dropdown" />
                        <label htmlFor="merchandise-warehouse">Entrepôt associé</label>
                    </span>
                </div>
                <div className="contentForm">
                    <span className="p-float-label">
                        <InputTextarea id="merchandise-description" value={form.description ?? ""} onChange={(e) => onChange("description", e.target.value)} rows={3} />
                        <label htmlFor="merchandise-description">Description</label>
                    </span>
                </div>
                <div className="contentForm">
                    <span className="p-float-label">
                        <InputTextarea id="merchandise-notes" value={form.notes ?? ""} onChange={(e) => onChange("notes", e.target.value)} rows={4} />
                        <label htmlFor="merchandise-notes">Notes internes</label>
                    </span>
                </div>
            </Dialog>

            <Dialog header="Modifier le mouvement" visible={!!editingMovement} modal className="inventory-dialog" style={{ width: "min(520px, 95vw)" }} footer={movementEditFooter} onHide={closeMovementEdit}>
                <div className="contentForm">
                    <span className="p-float-label">
                        <Dropdown inputId="movement-edit-direction" value={movementEditForm.direction} options={movementDirectionOptions} onChange={(e) => onMovementEditChange("direction", e.value)} className="inventory-dropdown" />
                        <label htmlFor="movement-edit-direction">Type de mouvement</label>
                    </span>
                    <span className="p-float-label">
                        <InputNumber inputId="movement-edit-quantity" value={movementEditForm.quantity} onValueChange={(e) => onMovementEditChange("quantity", e.value ?? 0)} useGrouping={false} min={1} />
                        <label htmlFor="movement-edit-quantity">Quantité</label>
                    </span>
                </div>
                <div className="contentForm">
                    <span className="p-float-label">
                        <Calendar inputId="movement-edit-date" value={movementEditForm.movedAt} onChange={(e) => onMovementEditChange("movedAt", e.value)} showIcon dateFormat="dd/mm/yy" />
                        <label htmlFor="movement-edit-date">Date</label>
                    </span>
                </div>
                <div className="contentForm">
                    <span className="p-float-label">
                        <InputTextarea id="movement-edit-notes" value={movementEditForm.notes ?? ""} onChange={(e) => onMovementEditChange("notes", e.target.value)} rows={3} />
                        <label htmlFor="movement-edit-notes">Note</label>
                    </span>
                </div>
            </Dialog>
        </div>
    );
}
